import React, { useState } from 'react';
import AdHocForecastControl from '../../ControlPanel/AdHocForecastControl';
import HelpTooltip from '../../HelpTooltip/HelpTooltip';
import { apiGet } from '../../../services_1/apiClient';
import { useAuth } from '../../../context/AuthContext';
import './AdHocWindow.css';

const ADJUSTMENT_TYPE_LABELS = {
  absolute: 'Absolute Value',
  percentage: 'Percentage Change',
  multiplier: 'Multiplier'
};

const REASON_CATEGORY_LABELS = {
  demand_shift: 'Demand Shift',
  market_event: 'Market Event',
  supply_constraint: 'Supply Constraint',
  promotional_impact: 'Promotional Impact',
  seasonal_adjustment: 'Seasonal Adjustment',
  external_factor: 'External Factor',
  data_correction: 'Data Correction',
  other: 'Other'
};

const AdHocWindow = ({ onAdHocAdjustment, loading, dataSummary, adHocData }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [currentForecastLoading, setCurrentForecastLoading] = React.useState(false);
  const [forecastResponse, setForecastResponse] = React.useState(null);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [adjustments, setAdjustments] = React.useState([]);
  const [sidebarLoading, setSidebarLoading] = React.useState(false);

  const approvalStatus = String(adHocData?.approval_status || adHocData?.status || '').toLowerCase();
  const isPendingApproval = approvalStatus === 'pending';
  const isAdminApproved = approvalStatus === 'approved';
  const isRejected = approvalStatus === 'rejected';
  const displayAdjustmentId = adHocData?.approved_adjustment_id || adHocData?.adjustment_id || '';
  const appliedTimestamp = adHocData?.applied_at || adHocData?.requested_at || adHocData?.timestamp || null;

  const fetchRecentAdjustments = async () => {
    setSidebarLoading(true);
    try {
      const data = await apiGet('/api/adhoc-requests?status=all');
      setAdjustments(Array.isArray(data?.requests) ? data.requests : []);
    } catch (err) {
      console.error('Failed to fetch recent ad hoc adjustments:', err);
    } finally {
      setSidebarLoading(false);
    }
  };

  const toggleSidebar = () => {
    const nextState = !sidebarOpen;
    setSidebarOpen(nextState);
    if (nextState) {
      fetchRecentAdjustments();
    }
  };

  React.useEffect(() => {
    if (sidebarOpen) {
      fetchRecentAdjustments();
    }
  }, [adHocData, sidebarOpen]);

  const fetchCurrentForecast = async (sku_id, channel, location, target_date) => {
    if (!sku_id) return null;

    setCurrentForecastLoading(true);
    setForecastResponse(null);

    try {
      const queryParams = new URLSearchParams({ sku_id });
      if (channel) queryParams.append('channel', channel);
      if (location) queryParams.append('location', location);
      if (target_date) queryParams.append('target_date', target_date);

      const data = await apiGet(`/api/current-forecast?${queryParams}`);
      setForecastResponse(data);
      return data?.current_forecast ?? null;
    } catch (error) {
      console.error('Error fetching current forecast:', error);
    } finally {
      setCurrentForecastLoading(false);
    }

    return null;
  };

  const suggestedForecast = forecastResponse?.current_forecast ?? null;
  const forecastMethodLabel = forecastResponse?.forecast_method_label ?? null;
  const forecastMethodKey = forecastResponse?.forecast_method ?? null;

  return (
    <div className="window-container adhoc-main-container">
      <div className="adhoc-left-content">
        <div className="window-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Ad-Hoc Adjustments</h2>
            <HelpTooltip
              title="Manual Forecast Overrides"
              content="Apply precise manual adjustments to specific SKUs on given dates. You can define qualitative reasons for these changes and view their absolute and percentage impact against the system baseline."
            />
          </div>
          {isAdmin && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={toggleSidebar}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              📋 {sidebarOpen ? 'Hide Recent Changes' : 'View Recent Ad Hoc Changes'}
            </button>
          )}
        </div>

        <div className="window-content">
          <AdHocForecastControl
            onAdHocAdjustment={onAdHocAdjustment}
            loading={loading}
            dataSummary={dataSummary}
            onFetchCurrentForecast={fetchCurrentForecast}
            currentForecastLoading={currentForecastLoading}
            suggestedForecast={suggestedForecast}
            forecastMethodLabel={forecastMethodLabel}
            forecastMethodKey={forecastMethodKey}
          />

          {adHocData && (
            <div className="results-section">
              <div className="results-header">
                <h3 className="results-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isPendingApproval
                    ? 'Adjustment Request Submitted'
                    : isRejected
                      ? 'Declined Request'
                      : isAdminApproved
                        ? 'Accepted Request'
                        : 'Adjustment Applied Successfully'}
                  <HelpTooltip content="A summary of your manual changes, including the previous baseline, the new adjusted value, and the percentage variance." />
                  {adHocData.persistence_status?.sync_queued && !adHocData.persistence_status?.dataset_saved && (
                    <span className="sync-badge sync-pending">Pending sync</span>
                  )}
                  {adHocData.persistence_status?.dataset_saved && (
                    <span className="sync-badge sync-done">Synced</span>
                  )}
                </h3>
              </div>
              <div className="results-content">
                {isPendingApproval && (
                  <div className="summary-item full-width warnings">
                    <label>Status:</label>
                    <span>Awaiting Admin Approval</span>
                  </div>
                )}
                {isAdminApproved && (
                  <div className="summary-item full-width approval-approved">
                    <label>Status:</label>
                    <span>Accepted request. The change has been approved and applied.</span>
                  </div>
                )}
                {isRejected && (
                  <div className="summary-item full-width approval-rejected">
                    <label>Status:</label>
                    <span>
                      Declined request
                      {adHocData.review_notes ? `: ${adHocData.review_notes}` : '.'}
                    </span>
                  </div>
                )}
                <div className="adjustment-summary">
                  {adHocData.request_id && (
                    <div className="summary-item">
                      <label>Request ID:</label>
                      <span className="adjustment-id">{adHocData.request_id}</span>
                    </div>
                  )}
                  <div className="summary-item">
                    <label>Adjustment ID:</label>
                    <span className="adjustment-id">{displayAdjustmentId || (isPendingApproval ? 'Waiting for admin approval' : 'N/A')}</span>
                  </div>
                  <div className="summary-item">
                    <label>SKU ID:</label>
                    <span>{adHocData.sku_id}</span>
                  </div>
                  <div className="summary-item">
                    <label>Target Forecast Date:</label>
                    <span>{adHocData.target_date}</span>
                  </div>
                  <div className="summary-item">
                    <label>Previous Value:</label>
                    <span>{(() => {
                      const val = adHocData.adjustment_summary?.previous_value ?? adHocData.current_forecast;
                      const num = Number(val);
                      return !isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val;
                    })()}</span>
                  </div>
                  <div className="summary-item">
                    <label>New Value:</label>
                    <span className="new-value">{(() => {
                      const val = adHocData.adjustment_summary?.new_value ?? adHocData.new_forecast_value;
                      const num = Number(val);
                      return !isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val;
                    })()}</span>
                  </div>
                  <div className="summary-item">
                    <label>Change:</label>
                    <span
                      className={`change-value ${(() => {
                        const previousVal = adHocData.adjustment_summary?.previous_value ?? adHocData.current_forecast;
                        const newVal = adHocData.adjustment_summary?.new_value ?? adHocData.new_forecast_value;
                        const delta = (typeof newVal === 'number' && typeof previousVal === 'number')
                          ? (newVal - previousVal)
                          : (adHocData.new_forecast_value - adHocData.current_forecast);
                        return Number(delta) < 0 ? 'negative' : 'positive';
                      })()}`}
                    >
                      {(() => {
                        const previousVal = adHocData.adjustment_summary?.previous_value ?? adHocData.current_forecast;
                        const newVal = adHocData.adjustment_summary?.new_value ?? adHocData.new_forecast_value;
                        const signedChange = (typeof newVal === 'number' && typeof previousVal === 'number')
                          ? (newVal - previousVal)
                          : (adHocData.new_forecast_value - adHocData.current_forecast);
                        const pct = adHocData.adjustment_summary?.change_percentage ??
                          (adHocData.current_forecast !== 0
                            ? (((adHocData.new_forecast_value - adHocData.current_forecast) / adHocData.current_forecast) * 100)
                            : null);
                        const deltaStr = typeof signedChange === 'number'
                          ? `${signedChange >= 0 ? '+' : ''}${signedChange.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : signedChange;
                        if (pct != null) {
                          const signedPct = (signedChange < 0 && Number(pct) > 0) ? -Number(pct) : Number(pct);
                          const pctStr = typeof signedPct === 'number' && !isNaN(signedPct)
                            ? `${signedPct >= 0 ? '+' : ''}${signedPct.toFixed(1)}%`
                            : pct;
                          return `${deltaStr} (${pctStr})`;
                        }
                        return deltaStr;
                      })()}
                    </span>
                  </div>
                  <div className="summary-item">
                    <label>Adjustment Value:</label>
                    <span className="adjustment-value">
                      {adHocData.adjustment_type === 'percentage'
                        ? `${adHocData.adjustment_value}%`
                        : adHocData.adjustment_type === 'multiplier'
                          ? `${adHocData.adjustment_value}x`
                          : adHocData.adjustment_value}
                    </span>
                  </div>
                  <div className="summary-item">
                    <label>Adjustment Type:</label>
                    <span>{ADJUSTMENT_TYPE_LABELS[adHocData.adjustment_type] || adHocData.adjustment_type}</span>
                  </div>
                  <div className="summary-item">
                    <label>Reason:</label>
                    <span>{REASON_CATEGORY_LABELS[adHocData.reason_category] || adHocData.reason_category}</span>
                  </div>

                  {adHocData.notes && (
                    <div className="summary-item full-width">
                      <label>Notes:</label>
                      <span>{adHocData.notes}</span>
                    </div>
                  )}

                  {adHocData.system_forecast_comparison && (
                    <>
                      <div className="summary-item">
                        <label>System Forecast:</label>
                        <span>{(() => {
                          const val = adHocData.adjustment_summary?.previous_value ?? adHocData.current_forecast;
                          const num = Number(val);
                          return !isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val;
                        })()}</span>
                      </div>
                      <div className="summary-item">
                        <label>vs System:</label>
                        <span className={`change-value ${(() => {
                          const previousVal = adHocData.adjustment_summary?.previous_value ?? adHocData.current_forecast;
                          const newVal = adHocData.adjustment_summary?.new_value ?? adHocData.new_forecast_value;
                          return (newVal - previousVal) >= 0 ? 'positive' : 'negative';
                        })()}`}>
                          {(() => {
                            const previousVal = adHocData.adjustment_summary?.previous_value ?? adHocData.current_forecast;
                            const newVal = adHocData.adjustment_summary?.new_value ?? adHocData.new_forecast_value;
                            const delta = newVal - previousVal;
                            const num = Number(delta);
                            return `${delta >= 0 ? '+' : ''}${!isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : delta}`;
                          })()}
                        </span>
                      </div>
                    </>
                  )}

                  {adHocData.promotional_context?.active_promotion && (
                    <div className="summary-item full-width promotional-context">
                      <label>Active Promotion:</label>
                      <span>
                        {adHocData.promotional_context.promo_details?.promo_name || 'Promotional period'}
                        {adHocData.promotional_context.promo_details?.discount_percentage &&
                          ` (${adHocData.promotional_context.promo_details.discount_percentage}% off)`}
                      </span>
                    </div>
                  )}

                  {adHocData.validation_warnings?.length > 0 && (
                    <div className="summary-item full-width warnings">
                      <label>Warnings:</label>
                      <ul>
                        {adHocData.validation_warnings.map((warning, index) => <li key={index}>{warning}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="summary-item">
                    <label>{isPendingApproval ? 'Request Submitted At:' : 'Applied At:'}</label>
                    <span>
                      {appliedTimestamp
                        ? `${new Date(appliedTimestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isAdmin && sidebarOpen && (
        <div className="adhoc-recent-sidebar">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--gray-200)', paddingBottom: '10px' }}>
            <h3>Recent Ad Hoc Changes</h3>
            <button className="adhoc-sidebar-close" onClick={toggleSidebar}>×</button>
          </div>
          {sidebarLoading ? (
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)' }}>
              <div className="spinner" style={{ width: '30px', height: '30px', marginRight: '10px' }}></div>
              Loading recent adjustments...
            </div>
          ) : adjustments.length === 0 ? (
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)', fontSize: '0.95rem' }}>
              No recent adjustments found.
            </div>
          ) : (
            <div className="adhoc-table-container">
              <table className="adhoc-table">
                <thead>
                  <tr>
                    <th>Change made</th>
                    <th>Amount</th>
                    <th>Done by</th>
                    <th>Date</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {[...adjustments]
                    .sort((a, b) => new Date(b.timestamp || b.applied_at || b.requested_at || 0) - new Date(a.timestamp || a.applied_at || a.requested_at || 0))
                    .map((adj, idx) => {
                    const sku = adj.sku_id;
                    const date = adj.target_date;
                    const scope = `${adj.channel || 'All'} @ ${adj.location || 'All'}`;
                    
                    let amountDisplay = '';
                    let isPositive = false;
                    let isNegative = false;
                    
                    const val = Number(adj.adjustment_value);
                    if (adj.adjustment_type === 'percentage') {
                      amountDisplay = `${val >= 0 ? '+' : ''}${val}%`;
                      isPositive = val > 0;
                      isNegative = val < 0;
                    } else if (adj.adjustment_type === 'multiplier') {
                      amountDisplay = `${val}x`;
                      isPositive = val > 1;
                      isNegative = val < 1;
                    } else {
                      // Absolute or other
                      amountDisplay = val.toLocaleString();
                      const prev = Number(adj.current_forecast || 0);
                      const next = Number(adj.new_forecast_value || 0);
                      isPositive = next > prev;
                      isNegative = next < prev;
                    }

                    const badgeClass = isPositive ? 'positive' : isNegative ? 'negative' : 'neutral';
                    const formattedPrev = Number(adj.current_forecast ?? 0).toLocaleString();
                    const formattedNext = Number(adj.new_forecast_value ?? 0).toLocaleString();
                    
                    const requester = adj.requested_by_user || adj.applied_by || 'User';
                    const reviewer = adj.reviewed_by || adj.applied_by || 'Admin';
                    
                    let doneByText = '';
                    let subText = '';
                    
                    if (adj.approval_status === 'pending') {
                      doneByText = requester;
                      subText = 'Pending Approval';
                    } else if (adj.reviewed_by) {
                      doneByText = requester;
                      subText = `Approved by ${reviewer}`;
                    } else {
                      // Self-applied
                      doneByText = requester;
                      const isDirectAdmin = adj.requested_by_role === 'admin' || adj.applied_by === 'admin';
                      subText = `Self-Applied${isDirectAdmin ? ' (Admin)' : ''}`;
                    }

                    const formattedDate = adj.timestamp || adj.applied_at || adj.requested_at
                      ? new Date(adj.timestamp || adj.applied_at || adj.requested_at).toLocaleDateString()
                      : '—';

                    return (
                      <tr key={adj.request_id || adj.adjustment_id || idx}>
                        <td>
                          <div className="adhoc-change-meta">
                            <span className="adhoc-sku-badge">{sku}</span>
                            <span className="adhoc-scope-text">{date}</span>
                            <span className="adhoc-scope-text">{scope}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`adhoc-amount-badge ${badgeClass}`}>{amountDisplay}</span>
                          <div className="adhoc-amount-transition">
                            {formattedPrev} → {formattedNext}
                          </div>
                        </td>
                        <td>
                          <div className="adhoc-user-badge">{doneByText}</div>
                          <span className="adhoc-role-label" style={{ color: subText.includes('Approved') ? 'var(--success)' : subText.includes('Pending') ? 'var(--warning)' : 'var(--gray-500)' }}>
                            {subText}
                          </span>
                        </td>
                        <td>
                          <span className="adhoc-date-text">{formattedDate}</span>
                        </td>
                        <td>
                          <div className="adhoc-reason-container">
                            <span className="adhoc-reason-badge">
                              {REASON_CATEGORY_LABELS[adj.reason_category] || adj.reason_category || 'Other'}
                            </span>
                            {adj.notes && <span className="adhoc-notes-text" title={adj.notes}>{adj.notes}</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdHocWindow;
